import { createApp } from 'vue';
import { IonicVue } from '@ionic/vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router';
import { appNavAnimation } from './router/page-transition';
import { installConfidentialScope } from './router/confidential-scope';
import { erpatClient, erpatClientKey } from '@/services/api/erpat-client';
import { setSessionExpiredHandler, setTokenRefreshedHandler } from '@/services/api/http';
import { useSessionStore } from '@/stores/session.store';
import { useUserStore } from '@/stores/user.store';
import { initPwa, setUpdateQuiesce } from '@/pwa/register';
import { offlineQuiesce } from '@/services/offline/offline-attendance';
import { initVersionCheck } from '@/pwa/version-check';
import { initInstallCapture } from '@/pwa/install';
import { initOfflineUi } from '@/pwa/offline-ui';
import { isMobileGateBlocked, mountMobileOnlyWall } from '@/pwa/mobile-gate';
import { captureDeepLink } from '@/services/realtime-navigation';
import { restoreBrandColor } from '@/theme/brand-color';
import { initAnalytics } from '@/services/analytics';

/* Ionic core CSS */
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Inter, self-hosted (latin subset) — replaces the Google Fonts <link> so the
   app renders identically offline and the render path loads no third-party
   origin (the one third-party load is analytics — lazy, after mount, and never
   render-blocking; see src/services/analytics.ts). The family name is
   unchanged, so --syntry-font in theme/tokens.scss still resolves. */
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-800.css';

/* Syntry theme */
import './theme/variables.scss';
import './theme/global.scss';
/* The shared toast's presentation. Paired with src/composables/useToast.ts —
   the `syntry-toast*` classes it builds are this file's contract. */
import './theme/toast.scss';

// Must run before the browser can fire `beforeinstallprompt`, which happens
// very early — hence module scope rather than inside the mount callback.
initInstallCapture();

// Re-apply the tenant's persisted brand color at module scope — i.e. after the
// theme SCSS imports above but before createApp/mount (which happens inside
// router.isReady()), so the overridden tokens are in place for the first paint
// and a non-teal tenant never flashes teal on reload.
restoreBrandColor();

/** The real app: router, session wiring, service worker, version watchdog. */
function bootApp(): void {
  // The deep link the page was opened with — the worker's cold-start
  // `#erpat-rt=<uuid>` fragment or `/tabs/inbox?id=<digits>` — is parked
  // HERE, before createApp and therefore before `.use(router)` starts the
  // initial navigation (which would otherwise be the last thing to see the
  // fragment). Deliberately inside bootApp, after the mobile gate: the wall
  // leaves the URL untouched so a desktop deep link survives copy-to-phone.
  // The fragment is stripped; the parked target is replayed by SignInPage
  // (login) or App.vue (restore) — see services/realtime-navigation.ts.
  captureDeepLink();

  const app = createApp(App)
    .use(IonicVue, { mode: 'ios', navAnimation: appNavAnimation })
    .use(createPinia())
    .use(router);

  // Make the ERPat client injectable in any component via inject(erpatClientKey).
  app.provide(erpatClientKey, erpatClient);

  // When a token can't be refreshed, reset session state (clears tokens, stops the
  // background refresher) and bounce to sign-in. Pinia is installed above, so the
  // store is resolvable here.
  const sessionStore = useSessionStore();
  setSessionExpiredHandler(() => {
    sessionStore.signOutLocal();
    void router.replace({ name: 'sign-in' });
  });

  // Re-sync the cached user identity (name, department, …) after every access-token
  // rotation, so a long-lived session always reflects the latest server profile.
  //
  // Gated on visibility, and that gate is load-bearing for PRESENCE, not just for saving a
  // request. The token refresher is deliberately driven by a Web Worker so it keeps rotating in
  // a backgrounded tab; every authenticated request now refreshes `users.last_online`, so an
  // ungated refresh here would make a tab left open behind another window blip "online" to the
  // whole company every ~15 minutes forever. Falsely-online for someone who is not there is the
  // worst failure a presence feature can have.
  //
  // Nothing is lost by deferring: the identity is already cached and nothing is rendering while
  // hidden, and the moment the tab is shown again the presence heartbeat fires and the user's own
  // navigation re-reads what it needs.
  setTokenRefreshedHandler(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    void useUserStore().refresh();
  });

  // Show/hide the global offline banner from the connectivity signal.
  initOfflineUi();

  router.isReady().then(() => {
    app.mount('#app');
    // An app update waits (bounded) for a punch being committed or a sync answer being settled.
    setUpdateQuiesce(offlineQuiesce);
    // Register the service worker (web + production only — see src/pwa/register.ts).
    initPwa();
    // Poll /version.json for a newer deployed build. Deliberately a separate call
    // rather than a line inside initPwa(): that function early-returns when the
    // service worker is unsupported, which is exactly a runtime the watchdog must
    // still cover.
    initVersionCheck();
    // Google Analytics for Firebase — screen views from the router, sign-in /
    // sign-out from the session store. After mount and isReady() so the very
    // first screen_view names the resolved route; loads its SDK chunk in the
    // background and is a no-op when VITE_FIREBASE_ANALYTICS=off. Deliberately
    // inside bootApp(): the mobile-only wall below is not the app.
    initAnalytics(router);
    // Re-lock a shared confidential unlock (the payslip list + preview) once
    // the user leaves its screens or the app sits in the background too long.
    installConfidentialScope(router);
  });
}

// Mobile-only gate. Decided here — after the module-scope install capture and
// brand restore above, and BEFORE createApp(App) — because `.use(router)` is
// what starts the initial navigation: on a desktop browser the router must
// never be installed, so no route (deep link, /tabs/*, catch-all), no guard,
// no session restore, no service worker and no watchdog ever runs behind the
// wall. See src/pwa/mobile-gate.ts for the detection + the QA bypass.
if (isMobileGateBlocked()) {
  mountMobileOnlyWall();
} else {
  bootApp();
}
